/**
 * ExportImportButtons — botões "Exportar JSON" + "Importar JSON" para
 * portabilidade de configurações admin entre ambientes (preview → produção).
 *
 * Props:
 *   - exportUrl:  string  ex. "/api/homepage-config/export"
 *   - importUrl:  string  ex. "/api/homepage-config/import"
 *   - filePrefix: string  ex. "homepage-config"
 *   - onImported: function  callback após import bem-sucedido
 *   - testidPrefix: string  ex. "homepage" → gera "homepage-export-btn"
 */
import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function ExportImportButtons({
  exportUrl,
  importUrl,
  filePrefix,
  onImported,
  testidPrefix = "config",
}) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await api.get(exportUrl);
      const payload = res.data;
      const now = new Date();
      const stamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("") + "-" + [
        String(now.getHours()).padStart(2, "0"),
        String(now.getMinutes()).padStart(2, "0"),
      ].join("");
      const filename = `${filePrefix}-${stamp}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado: ${filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao exportar");
    } finally {
      setBusy(false);
    }
  };

  const handleFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      toast.error("Ficheiro tem de ser .json");
      return;
    }
    // Confirm before overwrite — destructive action.
    const ok = window.confirm(
      `ATENÇÃO: vai substituir TODAS as definições deste separador pelo conteúdo de "${file.name}".\n\n` +
      `As definições atuais serão guardadas automaticamente como backup na base de dados.\n\n` +
      `Continuar?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await api.post(importUrl, payload);
      const backupNote = res.data?.backup_saved
        ? " (backup da versão anterior guardado)"
        : "";
      toast.success(`Importado com sucesso${backupNote}`);
      if (onImported) onImported();
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error("JSON inválido — verifica se o ficheiro está bem formado");
      } else {
        toast.error(err?.response?.data?.detail || "Erro ao importar");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={handleExport}
        disabled={busy}
        className="rounded-full"
        title="Descarregar todas as definições deste separador como .json"
        data-testid={`${testidPrefix}-export-btn`}
      >
        <Download className="w-4 h-4 mr-1.5" /> Exportar JSON
      </Button>
      <Button
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        disabled={busy}
        className="rounded-full"
        title="Restaurar definições a partir de um ficheiro .json exportado"
        data-testid={`${testidPrefix}-import-btn`}
      >
        <Upload className="w-4 h-4 mr-1.5" /> Importar JSON
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={handleFilePicked}
        className="hidden"
        data-testid={`${testidPrefix}-import-file-input`}
      />
    </>
  );
}
